import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import LiveWins from "../components/LiveWins";
import { sfx } from "../utils/sfx";
import { gamesApi } from "../services/api";
import { resolveGameCover } from "../utils/gameBanner";
import { useLanguage } from "../i18n/LanguageContext";
import { forgetGame, rememberGame, useRecentGames } from "../utils/recentGames";
import ProviderLogo from "../components/ProviderLogo";
import SlotShowcase from "../components/SlotShowcase";
import { useGamesCatalog } from "../context/GamesCatalogContext";
import { ORIGINAL_COVERS, originalCover } from "../utils/originalCovers";

const LOCAL_GAMES = [
  { game_id: "aviator", game_code: "local-aviator", game_name: "Aviator", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/aviator", banner: originalCover("aviator.png") },
  { game_id: "dice", game_code: "local-dice", game_name: "Zar", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/zar", banner: originalCover("zar.png") },
  { game_id: "plinko", game_code: "local-plinko", game_name: "Plinko", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/plinko", banner: originalCover("plinko.png") },
  { game_id: "mines", game_code: "local-mines", game_name: "Mayın", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/mayin", banner: originalCover("mines.png") },
  { game_id: "limbo", game_code: "local-limbo", game_name: "Limbo", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/limbo", banner: originalCover("limbo.png") },
  { game_id: "blackjack", game_code: "local-blackjack", game_name: "Blackjack", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/blackjack", banner: originalCover("blackjack.png") },
  { game_id: "wheel", game_code: "local-wheel", game_name: "Çark", provider_game: "Marsilya Orijinal", local: true, path: "/oyun/cark", banner: originalCover("wheel.png") },
];

const PAGE_SIZE = 96;
const HIDDEN_LOCAL = /hilo|hi-lo|coinflip|yazi-tura|yaz[iı] tura|\bokey\b|101 okey/i;
const fold = (value = "") =>
  String(value)
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const coverFor = (game) => {
  if (game.local) {
    const id = fold(game.game_id);
    const code = fold(game.game_code).replace(/^local-/, "");
    const name = fold(game.game_name).replace(/\s+/g, "-");
    const local = String(game.banner || "").startsWith("/") ? game.banner : "";
    return local || ORIGINAL_COVERS[id] || ORIGINAL_COVERS[code] || ORIGINAL_COVERS[name] || "";
  }
  return resolveGameCover(game, 480, true);
};

const isOriginal = (game) => game.local || /orijinal/i.test(game.provider_game);
const ORIG_SLUG = { zar: "dice", dice: "dice", mayin: "mines", mines: "mines", cark: "wheel", wheel: "wheel", aviator: "aviator", plinko: "plinko", limbo: "limbo", blackjack: "blackjack" };
const origSlugOf = (game) => ORIG_SLUG[fold(game.game_id).replace(/^local-/, "")] || fold(game.game_id);

const GameTile = ({ game, onOpen, badge, eagerCover = false, playLabel = "Oyna", demoLabel = "Demo", variant = "catalog", onRemove, removeLabel = "Kaldır" }) => {
  const cover = coverFor(game);
  const hasCover = Boolean(cover);
  const original = isOriginal(game);
  const origSlug = original ? origSlugOf(game) : "";
  const poster = original || variant === "recent";
  const imgRef = React.useRef(null);
  const [ready, setReady] = React.useState(!hasCover);
  const [broken, setBroken] = React.useState(false);
  const provider = String(game.provider_game || "")
    .replace(/\s*[·•]\s*RTP\s*\d+.*$/i, "")
    .replace(/pragmatic play.*/i, "Pragmatic Play")
    .trim();

  React.useEffect(() => {
    setReady(!hasCover);
    setBroken(false);
  }, [cover, hasCover]);

  React.useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setReady(true);
  }, [cover]);

  const loading = hasCover && !ready && !broken;
  const showCover = hasCover && !broken;

  const tile = (
    <button
      className={`game-card game-card--${variant}${poster ? ` orig-poster${origSlug ? ` orig-${origSlug}` : ""}` : ""}`}
      type="button"
      onClick={() => onOpen(game)}
    >
      <div className="game-card-shell">
        <div className={`game-card-media${loading ? " is-loading" : ""}${ready && showCover ? " is-ready" : ""}`}>
          {loading ? <div className="game-card-loader" aria-hidden /> : null}
          {showCover ? (
            <img
              ref={imgRef}
              className={`game-card-cover${ready ? " is-ready" : ""}`}
              src={cover}
              alt=""
              width={original ? 864 : 360}
              height={original ? 1152 : 480}
              referrerPolicy="no-referrer"
              loading={eagerCover ? "eager" : "lazy"}
              decoding="async"
              onLoad={() => setReady(true)}
              onError={() => {
                setBroken(true);
                setReady(true);
              }}
            />
          ) : (
            <div className="game-card-fallback">
              <span>{game.game_name}</span>
            </div>
          )}
          {badge ? <span className={`orig-badge${variant === "popular" || variant === "recent" ? " is-hot" : ""}`}>{badge}</span> : null}
          {poster ? <strong className="orig-title">{game.game_name}</strong> : null}
          <div className="game-card-overlay">
            <span className="play-btn" onClick={(event) => { event.stopPropagation(); onOpen(game, "real"); }}>{playLabel}</span>
            {!original && game.has_demo ? (
              <span className="play-btn demo" onClick={(event) => { event.stopPropagation(); onOpen(game, "fun"); }}>{demoLabel}</span>
            ) : null}
          </div>
        </div>
        {poster ? null : (
        <div className="game-card-plate">
          <strong title={game.game_name}>{game.game_name}</strong>
          {provider ? <small title={provider}>{provider}</small> : null}
        </div>
        )}
      </div>
    </button>
  );

  if (!onRemove) return tile;

  return (
    <div className={`game-card-wrap${poster ? " is-original" : ""}`}>
      <button
        type="button"
        className="game-card-forget"
        aria-label={removeLabel}
        title={removeLabel}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onRemove(game);
        }}
      >
        <CloseRoundedIcon fontSize="inherit" />
      </button>
      {tile}
    </div>
  );
};

const LobbyRail = ({ title, desc, to, seeAll, children, railClass = "", className = "" }) => {
  const scroller = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const childCount = React.Children.count(children);

  const update = () => {
    const el = scroller.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 6);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const run = () => update();
    run();
    const frame = requestAnimationFrame(run);
    const later = window.setTimeout(run, 250);
    window.addEventListener("resize", run);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(later);
      window.removeEventListener("resize", run);
    };
  }, [childCount]);

  const scrollByDir = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.78), behavior: "smooth" });
  };

  return (
    <section className={`lobby-section${className ? ` ${className}` : ""}`}>
      <div className="section-head lobby-rail-head">
        <div>
          <h2>{title}</h2>
          {desc ? <span>{desc}</span> : null}
        </div>
        <div className="lobby-rail-tools">
          {to ? (
            <Link className="section-more" to={to}>
              {seeAll}
            </Link>
          ) : null}
          <button type="button" className="rail-nav" disabled={!canLeft} onClick={() => scrollByDir(-1)} aria-label="Sola kaydır">
            <ChevronLeftIcon fontSize="small" />
          </button>
          <button type="button" className="rail-nav" disabled={!canRight} onClick={() => scrollByDir(1)} aria-label="Sağa kaydır">
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
      </div>
      <div className={`lobby-rail${railClass ? ` ${railClass}` : ""}`} ref={scroller} onScroll={update}>
        {children}
      </div>
    </section>
  );
};

const LobbyPage = ({ search = "", onSearch }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { providers } = useGamesCatalog();
  const { t } = useLanguage();
  const recent = useRecentGames();
  const [params] = useSearchParams();
  const type = params.get("type") || "";
  const queryFromUrl = params.get("q") || "";
  const providerFromUrl = params.get("provider") || "";
  const [provider, setProvider] = useState(providerFromUrl);

  useEffect(() => {
    setProvider(providerFromUrl);
  }, [providerFromUrl]);

  useEffect(() => {
    if (queryFromUrl) onSearch?.(queryFromUrl);
  }, [queryFromUrl, onSearch]);
  const [error, setError] = useState("");
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [popular, setPopular] = useState([]);
  const [slotsPreview, setSlotsPreview] = useState([]);
  const [livePreview, setLivePreview] = useState([]);
  const [homeRowsLoading, setHomeRowsLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const browsing = Boolean(type || search || provider);
  const studios = useMemo(
    () => (providers || []).filter((item) => Number(item.count) > 0 && item.name && !/orijinal/i.test(item.name)),
    [providers]
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [search, provider, type]);

  useEffect(() => {
    if (!browsing) {
      setGames([]);
      setTotal(0);
      setLoading(false);
      setReady(true);
      return undefined;
    }
    let active = true;
    setLoading(true);
    gamesApi
      .list({
        limit: visible,
        offset: 0,
        q: search || undefined,
        provider: provider || undefined,
        type: type || undefined,
      })
      .then((data) => {
        if (!active) return;
        setGames(data.games || []);
        setTotal(data.total || 0);
        setReady(true);
        setLoading(false);
        if (data.warning) setError(data.warning);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message);
        setLoading(false);
        setReady(true);
      });
    return () => {
      active = false;
    };
  }, [search, provider, type, visible, browsing]);

  useEffect(() => {
    if (browsing) return undefined;
    let active = true;
    setHomeRowsLoading(true);
    Promise.all([
      gamesApi.list({ type: "slot", limit: 48 }),
      gamesApi.list({ type: "casino", limit: 36 }),
    ])
      .then(([slotData, liveData]) => {
        if (!active) return;
        setSlotsPreview(slotData.games || []);
        setLivePreview(liveData.games || []);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setHomeRowsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [browsing]);

  useEffect(() => {
    let active = true;
    let timer = null;
    let attempt = 0;

    const finish = (items) => {
      if (!active) return;
      setPopular(items);
    };

    const loadPopular = () => {
      gamesApi
        .list({ popular: "1" })
        .then((data) => {
          if (!active) return;
          const items = data.games || [];
          if (items.length) {
            finish(items);
            return;
          }
          if (attempt < 8) {
            attempt += 1;
            timer = setTimeout(loadPopular, 1500);
            return;
          }
          finish([]);
        })
        .catch(() => {
          if (!active) return;
          if (attempt < 8) {
            attempt += 1;
            timer = setTimeout(loadPopular, 1500);
            return;
          }
          finish([]);
        });
    };

    loadPopular();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [ready]);

  const originals = useMemo(() => {
    if (type && type !== "original") return [];
    return LOCAL_GAMES.filter((game) => {
      if (HIDDEN_LOCAL.test(`${game.game_id} ${game.game_name} ${game.path || ""}`)) return false;
      if (!search) return true;
      return fold(`${game.game_name} ${game.provider_game}`).includes(fold(search));
    });
  }, [search, type]);

  const openGame = (game, mode = "real") => {
    rememberGame(game);
    const original = game.local || game.path || isOriginal(game);
    if (game.searchName && mode !== "fun") {
      navigate(`/?q=${encodeURIComponent(game.searchName)}`);
      return;
    }
    if (original || game.local || game.path) {
      sfx.click();
      navigate(game.path || `/oyun/${game.game_id}`);
      return;
    }
    const intent = mode === "fun" ? "fun" : "real";
    navigate(`/play/${encodeURIComponent(game.game_id)}`, { state: { game, intent } });
  };

  const slotRow = (slotsPreview.length ? slotsPreview : popular).slice(0, 48);
  const liveRow = livePreview.slice(0, 36);
  const featured = (popular.length ? popular : slotRow).slice(0, 10);

  return (
    <Box className="lobby">
      <section className="lobby-hero">
        <div className="hero-copy">
          <h1>
            {t("lobby.heroTitlePrefix") ? <>{t("lobby.heroTitlePrefix")} </> : null}
            <em>{t("lobby.heroTitleHighlight")}</em>
            {t("lobby.heroTitleInline") ? <> {t("lobby.heroTitleInline")}</> : null}
            {t("lobby.heroTitleLine2") ? (
              <>
                <br />
                {t("lobby.heroTitleLine2")}
              </>
            ) : null}
          </h1>
          <p>{t("lobby.heroDesc")}</p>
          <div className="hero-actions">
            <Link className="hero-cta" to="/hesabim?tab=deposit">
              {t("lobby.heroClaim")}
            </Link>
            <Link className="hero-cta ghost" to="/oyna">
              {t("lobby.playNow")}
            </Link>
          </div>
        </div>
      </section>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      <label className="lobby-search-mobile">
        <SearchIcon />
        <input
          value={search}
          onChange={(event) => onSearch?.(event.target.value)}
          placeholder={t("header.search")}
        />
      </label>

      {!browsing ? (
        <>
          {recent.length ? (
            <LobbyRail title={t("lobby.recent")} desc={t("lobby.recentDesc")} railClass="is-recent">
              {recent.map((game) => (
                <GameTile
                  key={`recent-${game.path || game.game_id}`}
                  game={game}
                  onOpen={openGame}
                  onRemove={forgetGame}
                  removeLabel={t("lobby.removeRecent")}
                  eagerCover
                  variant="recent"
                  badge={isOriginal(game) ? t("badge.original") : t("badge.popular")}
                  playLabel={t("common.play")}
                  demoLabel={t("common.demo")}
                />
              ))}
            </LobbyRail>
          ) : null}

          <LobbyRail
            className="originals-section"
            railClass="is-originals"
            title={t("lobby.marsilyaGames")}
            desc={t("lobby.marsilyaGamesDesc")}
            to="/?type=original"
            seeAll={t("lobby.seeAll")}
          >
            {originals.map((game) => (
              <GameTile
                key={`orig-${game.game_id}`}
                game={game}
                onOpen={openGame}
                eagerCover
                variant="original"
                playLabel={t("common.play")}
                demoLabel={t("common.demo")}
              />
            ))}
          </LobbyRail>

          <LobbyRail title={t("lobby.slots")} desc={t("lobby.slotsDesc")} to="/?type=slot" seeAll={t("lobby.seeAll")}>
            {homeRowsLoading && !slotRow.length
              ? Array.from({ length: 8 }).map((_, index) => (
                  <div key={`slot-skel-${index}`} className="game-card game-card--popular game-card-skeleton" aria-hidden />
                ))
              : slotRow.map((game) => (
                  <GameTile
                    key={`slot-${game.game_id}`}
                    game={game}
                    onOpen={openGame}
                    eagerCover
                    variant="popular"
                    playLabel={t("common.play")}
                    demoLabel={t("common.demo")}
                  />
                ))}
          </LobbyRail>

          <LobbyRail title={t("lobby.liveCasino")} desc={t("lobby.liveCasinoDesc")} to="/?type=casino" seeAll={t("lobby.seeAll")}>
            {homeRowsLoading && !liveRow.length
              ? Array.from({ length: 8 }).map((_, index) => (
                  <div key={`live-skel-${index}`} className="game-card game-card-skeleton" aria-hidden />
                ))
              : liveRow.map((game) => (
                  <GameTile
                    key={`live-${game.game_id}`}
                    game={game}
                    onOpen={openGame}
                    playLabel={t("common.play")}
                    demoLabel={t("common.demo")}
                  />
                ))}
          </LobbyRail>

          <SlotShowcase
            games={featured}
            onOpen={openGame}
            title={t("lobby.featured")}
            moreLabel={t("lobby.seeAll")}
            moreTo="/?type=slot"
            playLabel={t("common.play")}
          />
        </>
      ) : (
        <>
          {type !== "casino" && type !== "slot" && originals.length ? (
            <section className="lobby-section originals-section">
              <div className="section-head">
                <div>
                  <h2>{t("lobby.marsilyaGames")}</h2>
                  <span>{t("lobby.marsilyaGamesDesc")}</span>
                </div>
              </div>
              <div className="originals originals-full">
                {originals.map((game) => (
                  <GameTile key={`orig-${game.game_id}`} game={game} onOpen={openGame} eagerCover variant="original" playLabel={t("common.play")} demoLabel={t("common.demo")} />
                ))}
              </div>
            </section>
          ) : null}

          {type === "original" ? null : (
            <>
          <div className="section-head">
            <div>
              <h2>{type === "casino" ? t("lobby.liveCasino") : type === "slot" ? t("lobby.slots") : t("lobby.allGames")}</h2>
              <span>{loading ? t("lobby.gamesLoading") : `${t("lobby.totalGames", { total: total.toLocaleString("tr-TR") })} · ${t("lobby.showing", { count: games.length })}`}</span>
            </div>
          </div>
          <div className="filters">
            <button type="button" className={`filter-chip${!provider ? " active" : ""}`} onClick={() => navigate(type ? `/?type=${type}` : "/")}>
              {t("lobby.filterAll")}
            </button>
            {provider ? (
              <button
                type="button"
                className="filter-chip active"
                onClick={() => navigate(type ? `/?type=${type}` : "/")}
                title="Sağlayıcı filtresini temizle"
              >
                {provider} ×
              </button>
            ) : null}
          </div>

          {loading && !games.length ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("lobby.gamesLoading")}
            </Alert>
          ) : null}
          {ready && !loading && games.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Bu filtreye uyan oyun yok. Aramayı veya sağlayıcıyı temizle.
            </Alert>
          ) : null}

          <div className="game-grid">
            {games.map((game) => (
              <GameTile key={`${game.provider_game}-${game.game_id}`} game={game} onOpen={openGame} playLabel={t("common.play")} demoLabel={t("common.demo")} />
            ))}
          </div>

          {games.length < total ? (
            <Button sx={{ mt: 2 }} variant="outlined" color="secondary" disabled={loading} onClick={() => setVisible((value) => value + PAGE_SIZE)}>
              {t("lobby.loadMore", { count: (total - games.length).toLocaleString("tr-TR") })}
            </Button>
          ) : null}
            </>
          )}
        </>
      )}

      {studios.length ? (
        <LobbyRail title={t("lobby.providers")} to="/saglayicilar" seeAll={t("lobby.seeAll")} railClass="is-providers">
          {studios.map((item) => (
            <Link
              key={item.code || item.name}
              to={`/?provider=${encodeURIComponent(item.name)}`}
              className={`prov-slide${/egt/i.test(item.name) ? " is-egt" : ""}`}
              aria-label={item.name}
            >
              <ProviderLogo name={item.name} src={item.logo} className={/egt/i.test(item.name) ? "is-egt-wide" : ""} />
            </Link>
          ))}
        </LobbyRail>
      ) : null}

      <LiveWins />
    </Box>
  );
};

export default LobbyPage;
